// Trading Book -> P&L analytics (UI/UX audit Phase 4): the realised equity curve, drawdown and expectancy, and
// where the P&L came from (by strategy, by exit reason). Every figure is the backend's (paper_book.analytics);
// nothing is recomputed here.
import React, { useMemo, useState } from "react";
import { inrPnl } from "@/lib/format";
import { exitText } from "@/lib/labels";

const pnlCls = (v) => (v == null ? "text-slate-500" : v > 0 ? "text-[#00C805]" : v < 0 ? "text-[#FF6b62]" : "text-slate-300");
const W = 640, H = 140, PAD = { l: 8, r: 8, t: 10, b: 18 };

/** Cumulative realised P&L after each completion day: one series, one hue, a zero baseline, a hover readout. */
export function EquityChart({ points, testId = "pnl-equity" }) {
  const [hover, setHover] = useState(null);
  const geo = useMemo(() => {
    if (!points?.length) return null;
    const vals = points.map((p) => p.cumulative_net);
    const lo = Math.min(0, ...vals), hi = Math.max(0, ...vals);
    const span = hi - lo || 1;
    const x = (i) => PAD.l + (points.length === 1 ? (W - PAD.l - PAD.r) / 2 : (i * (W - PAD.l - PAD.r)) / (points.length - 1));
    const y = (v) => PAD.t + ((hi - v) * (H - PAD.t - PAD.b)) / span;
    return { x, y, zero: y(0), path: points.map((p, i) => `${i ? "L" : "M"}${x(i).toFixed(1)},${y(p.cumulative_net).toFixed(1)}`).join(" ") };
  }, [points]);
  if (!geo) return null;
  const last = points[points.length - 1];
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const px = ((e.clientX - r.left) / r.width) * W;
    let best = 0;
    points.forEach((_, i) => { if (Math.abs(geo.x(i) - px) < Math.abs(geo.x(best) - px)) best = i; });
    setHover(best);
  };
  const h = hover != null ? points[hover] : null;
  return (
    <figure className="term-panel rounded-sm p-2 m-0" data-testid={testId}>
      <figcaption className="flex items-baseline justify-between text-[11px] px-1">
        <span className="font-bold text-slate-300">Realised P&L, cumulative (net of charges)</span>
        <span className="mono text-slate-400" data-testid={`${testId}-readout`}>
          {h ? <>{h.date} · <span className={pnlCls(h.cumulative_net)}>{inrPnl(h.cumulative_net, 0)}</span></>
            : <>to {last.date} · <span className={pnlCls(last.cumulative_net)}>{inrPnl(last.cumulative_net, 0)}</span></>}
        </span>
      </figcaption>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-36" role="img" onMouseMove={onMove} onMouseLeave={() => setHover(null)}
        aria-label={`Cumulative realised P&L from ${points[0].date} to ${last.date}, now ${inrPnl(last.cumulative_net, 0)}`}>
        <line x1={PAD.l} x2={W - PAD.r} y1={geo.zero} y2={geo.zero} stroke="var(--term-border-strong, #3a4150)" strokeWidth="1" strokeDasharray="3 3" />
        <text x={W - PAD.r} y={geo.zero - 3} textAnchor="end" fontSize="9" fill="var(--term-muted, #64748b)">0</text>
        <path d={geo.path} fill="none" stroke="#5b9bff" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        {h && <>
          <line x1={geo.x(hover)} x2={geo.x(hover)} y1={PAD.t} y2={H - PAD.b} stroke="var(--term-muted, #64748b)" strokeWidth="1" />
          <circle cx={geo.x(hover)} cy={geo.y(h.cumulative_net)} r="4" fill="#5b9bff" stroke="var(--term-s1, #12151C)" strokeWidth="2" />
        </>}
        <text x={PAD.l} y={H - 4} fontSize="9" fill="var(--term-muted, #64748b)">{points[0].date}</text>
        <text x={W - PAD.r} y={H - 4} textAnchor="end" fontSize="9" fill="var(--term-muted, #64748b)">{last.date}</text>
      </svg>
    </figure>
  );
}

export function AnalyticsStrip({ a, Stat }) {
  if (!a) return null;
  return (
    <div className="grid gap-1.5 grid-cols-2 sm:grid-cols-5" data-testid="pnl-analytics">
      <Stat label="Max drawdown" value={inrPnl(a.max_drawdown, 0)} cls={pnlCls(a.max_drawdown)}
        sub="Largest fall of realised P&L from a peak" testId="an-drawdown" />
      <Stat label="Expectancy" value={a.expectancy == null ? "—" : inrPnl(a.expectancy, 0)} cls={pnlCls(a.expectancy)}
        sub="Average net per completed trade" testId="an-expectancy" />
      <Stat label="Avg win" value={a.avg_win == null ? "—" : inrPnl(a.avg_win, 0)} cls="text-[#00C805]" testId="an-avg-win" />
      <Stat label="Avg loss" value={a.avg_loss == null ? "—" : inrPnl(a.avg_loss, 0)} cls="text-[#FF6b62]" testId="an-avg-loss" />
      <Stat label="Profit factor" value={a.profit_factor == null ? "—" : a.profit_factor.toFixed(2)}
        cls={a.profit_factor == null ? "text-slate-400" : a.profit_factor >= 1 ? "text-[#00C805]" : "text-[#FF6b62]"}
        sub="Gross wins ÷ gross losses" testId="an-profit-factor" />
    </div>
  );
}

/** By strategy / by exit reason: trades, winners and net P&L, worst first. */
export function BreakdownTable({ rows, label, exitCodes = false, testId }) {
  return (
    <div className="overflow-auto rounded-sm border border-[#2A2F3A]" data-testid={testId}>
      <table className="w-full text-[11px] mono">
        <thead className="bg-[#12151C] text-[10px] uppercase tracking-wide text-slate-500"><tr>
          {[label, "Trades", "Won", "Win rate", "Net P&L"].map((h, i) => <th key={h} className={`px-2 py-1.5 ${i ? "text-right" : "text-left"}`}>{h}</th>)}
        </tr></thead>
        <tbody>
          {(rows || []).length === 0 && <tr><td colSpan={5} className="px-3 py-4 text-center text-slate-600">No completed trades in this period.</td></tr>}
          {(rows || []).map((r) => (
            <tr key={r.key} className="gz-row">
              <td className="px-2 py-1 text-slate-200" title={exitCodes ? r.key : undefined}>{exitCodes ? exitText(r.key) : r.key}</td>
              <td className="px-2 py-1 text-right">{r.trades}</td>
              <td className="px-2 py-1 text-right">{r.winners}</td>
              <td className="px-2 py-1 text-right">{r.trades ? `${((r.winners * 100) / r.trades).toFixed(0)}%` : "—"}</td>
              <td className={`px-2 py-1 text-right font-bold ${pnlCls(r.net_pnl)}`}>{inrPnl(r.net_pnl, 0)}</td>
            </tr>))}
        </tbody>
      </table>
    </div>
  );
}
